/**
 * Client content pack (P4.9) — the presentation-only projection of the
 * validated content set, plus the hash-handshake helpers.
 *
 * Clients need presentation data only (docs/architecture/content-pipeline.md
 * "Client Content Pack"). This module derives that pack from the same validated
 * definitions the server loads, by an ALLOW-LIST projection: each category lists
 * exactly the presentation/identity fields that may ship. A field absent from
 * the list is excluded by construction — so a *future* server-only field can
 * never leak into the pack without someone deliberately adding it here (the
 * P4.9 design intent: "allow-list the presentation fields, don't deny-list the
 * secret ones"). Server tuning therefore never ships: loot/drop tables, spawn
 * weights, scarcity bounds/multipliers, recipe outcome maths + proc odds, enemy
 * behaviour + stats, NPC vendor/behaviour data, biome generation/spawn tables.
 *
 * Browser-safe: pure projection + canonical serialisation only, no node:crypto
 * (the hash is computed by the node-only `buildClientPack` in load-node.ts, which
 * the server and the build tool both call — guaranteeing identical bytes/hash).
 * The client never builds the pack; it downloads it and compares hashes.
 */
import { CONTENT_CATEGORIES } from './types.js';
/** Pack format version (bump only on a breaking pack-shape change). */
export const CLIENT_PACK_VERSION = 1;
/** Identity + presentation fields every category may ship. */
const BASE_FIELDS = ['id', 'name', 'description', 'presentation'];
/**
 * Per-category presentation allow-list. THE security boundary of the pack:
 * only these top-level fields are projected; everything else (including any
 * field added to a model later) is dropped. Keep additions presentation-only.
 */
export const PRESENTATION_FIELDS = {
    // Wardrobe catalogue ships to the client so the character-creation picker is
    // data-driven (id/name/slot + the art asset family). Power-neutral, no tuning.
    'appearance-options': [...BASE_FIELDS, 'slot', 'assetRef'],
    // `lowQualityBase` (q-flora-low-tier-textures): which band a low-quality client draws.
    // `emitters` (q-npc-props-adoption-and-flame): runtime flames the client draws on an asset.
    'asset-packs': [...BASE_FIELDS, 'assets', 'lowQualityBase', 'emitters'],
    // Biome presentation/identity, plus the SUB-PROJECTED `generation` the client
    // draws its dressing from (AD-315): scatter rules + settlement kit layouts +
    // the bound terrain set id (AD-408) only — see BIOME_GENERATION_FIELDS for
    // exactly what survives and why the rest (provenance/seed/placement/navmesh/
    // nodeKit/roads) does not.
    // Spawn tables (nodeTable/enemyTable), `seed` and streamingCells never ship;
    // the live world (collision, shrines, transitions, POIs) is delivered via
    // helloAck.world. `tier` (public danger band, as enemies/items already ship it)
    // selects the zone's presentational population weight (E3.3.S6).
    biomes: [...BASE_FIELDS, 'biomeType', 'tier', 'generation'],
    // Cosmetic identity + the weapon-skin overlay binding (`kind`/`slot`/`appliesTo`,
    // docs/systems/weapon-skins.md) so the client can resolve a preferred skin
    // against the equipped item's archetype. No price/class/odds ship here — the
    // P5.7 storefront adds the published Cloud Cache odds (AD-024 requires them in-client).
    cosmetics: [...BASE_FIELDS, 'kind', 'slot', 'appliesTo'],
    // DR-025 (AD-692 Decision 5): presentation only. `position` /
    // `rotationQuarterTurns` / `clearanceRadius` never ship here — placed sites reach
    // the client with the live world (helloAck.world, E4.6.S3); `themes` stays server-side.
    dungeons: [...BASE_FIELDS, 'biome', 'tier', 'difficulty'],
    // Enemy display + telegraph VISUALS; `attacks` is sub-projected to drop damage.
    // archetypes (behaviour), stats and loot/yields (tables) never ship.
    enemies: [...BASE_FIELDS, 'tier', 'level', 'isBoss', 'factions', 'attacks'],
    factions: [...BASE_FIELDS, 'reputationRanks', 'visualIdentity'],
    // Item display/UI. baseStats/effectSlots/maxDurability are gameplay (P6.4) and
    // baseValue is tuning — none ship at the slice; P6.4 may add baseStats here.
    items: [...BASE_FIELDS, 'kind', 'tier', 'rarity', 'slot', 'stackable', 'tradeable'],
    // Server-only placement geometry (the baked walkable navmesh, WR.4.3): an EMPTY
    // allow-list ⇒ projectClientPack omits the category from the pack entirely, so
    // the polygons never ship (the client renders/derives nothing from them yet).
    navmeshes: [],
    // NPC dialogue/identity. vendorStock (P5 shop) and live position (helloAck.npcs) excluded.
    // `ambient` = the NPC's animation set (q-npc-ambient-animations; presentation only).
    npcs: [...BASE_FIELDS, 'roles', 'faction', 'biome', 'dialogue', 'ambient'],
    // Quest tracker content (objectives + rewards are player-facing). No hidden internals in the slice schema.
    quests: [
        ...BASE_FIELDS,
        'giver',
        'turnIn',
        'minLevel',
        'prerequisite',
        'objectives',
        'rewards',
        'dialogue',
    ],
    raids: [...BASE_FIELDS],
    // Crafting-UI fields. `outcome` (quality weights/variance/proc odds = recipe maths)
    // and `successor` (lifecycle) never ship.
    recipes: [
        ...BASE_FIELDS,
        'discipline',
        'station',
        'masteryRank',
        'craftTimeSec',
        'stationFee',
        'inputs',
        'output',
        'outputQuantity',
        'retired',
    ],
    // Resource display/UI. baseValue (tuning) and scarcity (bounds) never ship.
    resources: [...BASE_FIELDS, 'tier', 'gatheringSkill', 'qualityRange', 'stackable'],
    // Terrain foundation (DR-021/AD-200): elevation only (AD-408). The grid
    // params + `tiles[]` sub-projected to `tx`/`tz`/`elevation` (TERRAIN_TILE_FIELDS)
    // — the client derives its heightfield ground from them. The climate rasters,
    // `seed`, `hash`, `tileCount` and bake provenance never ship.
    'terrain-tiles': [...BASE_FIELDS, 'origin', 'metersPerPixel', 'tileSizePx', 'tiles'],
    themes: [...BASE_FIELDS, 'palette', 'lighting', 'fog', 'skybox', 'ambientAudio', 'weather'],
};
/**
 * Nested sub-projection for enemy telegraphed attacks: keep the VISUAL (shape +
 * timing the client renders, networking.md) and drop `damage` (combat tuning).
 * `motion` (AD-636) is presentation too: it picks the rig's `Attack_<motion>` clip.
 */
const ENEMY_ATTACK_FIELDS = ['id', 'name', 'weight', 'windupMs', 'radius', 'motion'];
/**
 * Nested sub-projection for a terrain tile (AD-408): the ELEVATION raster and
 * its tile address only. `temperature`/`humidity` have no client consumer and
 * never ship (the AD-044 reservation discipline).
 */
const TERRAIN_TILE_FIELDS = ['tx', 'tz', 'elevation'];
/**
 * Nested sub-projection for a biome's `generation` (AD-315) — the enemy-attack
 * pattern applied to the one server-side lump the client now genuinely consumes.
 *
 * SHIPS: `scatter` (the authored dressing rules), `settlements` (the
 * buildings the player walks around — their collision already ships in the
 * join-ack descriptor, so the transforms leak nothing new) and `terrain` (the
 * bound tile-set id, AD-408 — it resolves to the projected `terrain-tiles` set
 * the client seats its ground on).
 *
 * DOES NOT SHIP, deliberately: `provenance` (generation prompts/models — dev
 * material), `seed` (a server reproducibility handle), `placement` (node/enemy
 * placement TUNING; their positions arrive on the wire), `navmesh` / `nodeKit`
 * (server/pipeline handles whose categories ship nothing, so the ids would
 * dangle), and `roads` (no client consumer yet — the AD-044 reservation
 * discipline: ship it when something draws it).
 */
const BIOME_GENERATION_FIELDS = ['scatter', 'settlements', 'terrain'];
/**
 * Scatter-rule fields the client needs to derive placements. `terrain`
 * conditions are dropped: they evaluate against elevation/climate rasters that
 * never ship, and a condition that cannot be evaluated must not silently change
 * placement (AD-315 §2).
 */
const SCATTER_RULE_FIELDS = [
    'assetPack',
    'surface',
    'density',
    'avoid',
    'exclude',
    'clearance',
];
/** Settlement fields the client needs to draw the kit-bash (and its pieces). */
const SETTLEMENT_FIELDS = [
    'id',
    'assetPack',
    'position',
    'footprintRadius',
    'faction',
    'pieces',
];
/** Kit-piece transform fields — exactly what the shared placement law reads. */
// `station` (q-player-names-and-wayfinding): which crafting station a placed bench IS, so the
// client can point a craft objective at it. Presentation/identity only (the zone's station set
// is already public through every craft the server accepts there).
const STRUCTURE_PIECE_FIELDS = ['asset', 'x', 'y', 'z', 'rotation', 'station'];
/** Sub-project each element of an array-valued field through an allow-list. */
function pickEach(value, allowed) {
    if (!Array.isArray(value))
        return value;
    return value.map((entry) => entry !== null && typeof entry === 'object'
        ? pickFields(entry, allowed)
        : entry);
}
/** Project a biome's `generation` down to the client-consumed arms. */
function projectGeneration(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const out = pickFields(value, BIOME_GENERATION_FIELDS);
    // An empty arm is pack bytes with no meaning — drop it, so a biome that
    // authors only server-side generation ships no `generation` at all.
    for (const key of BIOME_GENERATION_FIELDS) {
        const arm = out[key];
        if (Array.isArray(arm) && arm.length === 0)
            delete out[key];
    }
    if (out['scatter'] !== undefined)
        out['scatter'] = pickEach(out['scatter'], SCATTER_RULE_FIELDS);
    if (out['settlements'] !== undefined) {
        const settlements = pickEach(out['settlements'], SETTLEMENT_FIELDS);
        out['settlements'] = Array.isArray(settlements)
            ? settlements.map((settlement) => {
                if (settlement === null || typeof settlement !== 'object')
                    return settlement;
                const record = settlement;
                if (record['pieces'] !== undefined) {
                    record['pieces'] = pickEach(record['pieces'], STRUCTURE_PIECE_FIELDS);
                }
                return record;
            })
            : settlements;
    }
    return Object.keys(out).length === 0 ? undefined : out;
}
function pickFields(source, allowed) {
    const out = {};
    for (const key of allowed) {
        const value = source[key];
        if (value !== undefined)
            out[key] = value;
    }
    return out;
}
/** Project one definition to its presentation fields (with nested sub-projections). */
export function projectDef(category, def) {
    const out = pickFields(def, PRESENTATION_FIELDS[category]);
    if (category === 'enemies' && Array.isArray(out['attacks'])) {
        out['attacks'] = pickEach(out['attacks'], ENEMY_ATTACK_FIELDS);
    }
    if (category === 'terrain-tiles' && Array.isArray(out['tiles'])) {
        out['tiles'] = pickEach(out['tiles'], TERRAIN_TILE_FIELDS);
    }
    if (category === 'biomes' && out['generation'] !== undefined) {
        const generation = projectGeneration(out['generation']);
        if (generation === undefined)
            delete out['generation'];
        else
            out['generation'] = generation;
    }
    return out;
}
/** Project the whole validated content set into the presentation pack. */
export function projectClientPack(defs) {
    const categories = {};
    for (const category of CONTENT_CATEGORIES) {
        const catDefs = defs.get(category);
        if (!catDefs || catDefs.size === 0)
            continue;
        // An empty presentation allow-list means the category ships nothing — server-
        // only data (e.g. navmeshes, WR.4.3) is omitted from the pack entirely.
        if (PRESENTATION_FIELDS[category].length === 0)
            continue;
        const projected = {};
        for (const id of [...catDefs.keys()].sort((a, b) => a.localeCompare(b))) {
            const def = catDefs.get(id);
            if (def !== undefined)
                projected[id] = projectDef(category, def);
        }
        categories[category] = projected;
    }
    return { version: CLIENT_PACK_VERSION, categories };
}
/**
 * Deterministic JSON: object keys sorted recursively, undefined-valued keys
 * dropped. The pack hash is taken over these exact bytes, so the server (boot)
 * and the build tool produce an identical hash from identical content.
 */
export function canonicalJsonStringify(value) {
    if (value === undefined)
        return 'null';
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value);
    if (Array.isArray(value))
        return `[${value.map(canonicalJsonStringify).join(',')}]`;
    const obj = value;
    const keys = Object.keys(obj)
        .filter((k) => obj[k] !== undefined)
        .sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJsonStringify(obj[k])}`).join(',')}}`;
}
// ---------------------------------------------------------------------------
// Hash handshake (client side). The server announces the required pack hash in
// helloAck.contentPackHash; the client compares it to the hash of the pack it
// has loaded and refetches over HTTPS on a mismatch (content-pipeline.md).
// ---------------------------------------------------------------------------
/**
 * Whether the client must refetch. False when the server announces no pack
 * (`''`, e.g. an empty registry) — there is nothing to fetch. Otherwise true
 * whenever the announced hash differs from what the client currently holds
 * (including the first join, when the client holds none).
 */
export function shouldRefetchPack(currentHash, announcedHash) {
    if (!announcedHash)
        return false;
    return currentHash !== announcedHash;
}
/**
 * Reconcile the client's pack against the server-announced hash, fetching via
 * the injected `fetchPack` only on a mismatch. Pure orchestration (no globals,
 * no I/O of its own) so the detect-and-refetch behaviour is unit-testable; the
 * frontend ContentPackStore wraps it with the real HTTPS fetch.
 */
export async function syncContentPack(current, announcedHash, fetchPack) {
    if (!shouldRefetchPack(current.hash, announcedHash)) {
        return { refetched: false, state: current };
    }
    const pack = await fetchPack(announcedHash);
    return { refetched: true, state: { hash: announcedHash, pack } };
}
//# sourceMappingURL=client-pack.js.map